import React, { useState } from "react";
import { Button, Dropdown, Input, Menu, Tooltip } from "antd";
import {
  BoldOutlined,
  DownOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useSlate } from "slate-react";
import classNames from "classnames";
import { EComModal } from "components/EComModal";
import {
  CLEAR_FORMATTING_HINT,
  HEADING_HINT,
  HEADING_LABEL,
  HEADING_NORMAL_LABEL,
  INSERT_LINK_HINT,
  LINK_MODAL_OK_TEXT,
  LINK_MODAL_TITLE,
  LINK_URL_PLACEHOLDER,
  MARK_HINT,
  ORDERED_LIST_HINT,
  UNORDERED_LIST_HINT,
} from "./configs";
import {
  HEADING_LEVELS,
  PromptListType,
  activeHeadingLevel,
  clearFormatting,
  insertLink,
  isListActive,
  isMarkActive,
  setHeading,
  toggleList,
  toggleMark,
} from "./slate";
import { PromptMark } from "./markdown";
import styles from "./PromptEditor.module.scss";

/**
 * 🔴 Underline is here because it was asked for, and markdown has no syntax for it — it is written as inline
 * HTML (`<u>`) and read back the same way, which is a decision recorded in `promptMarkdown.ts`. Bold and
 * italic are markdown's own. None of the three changes what an LLM does with the prompt; they are for the
 * person reading it.
 *
 * 🔴 HEADINGS HAVE A MENU NOW (the BA's request), which the original set deliberately left out. Prompts are
 * pasted in from documents that use them, and a prompt with headings could be read but not written — so the
 * one construct the parser handles and the toolbar refused is now offered. Tables and quotes stay out: those
 * the editor cannot show at all, and a button for one would be a way to make a prompt unsavable.
 */

const MARKS: { mark: PromptMark; icon: React.ReactNode }[] = [
  { mark: "bold", icon: <BoldOutlined /> },
  { mark: "italic", icon: <ItalicOutlined /> },
  { mark: "underline", icon: <UnderlineOutlined /> },
];

const LISTS: { type: PromptListType; icon: React.ReactNode; hint: string }[] = [
  { type: "bulleted-list", icon: <UnorderedListOutlined />, hint: UNORDERED_LIST_HINT },
  { type: "numbered-list", icon: <OrderedListOutlined />, hint: ORDERED_LIST_HINT },
];

const ClearFormattingIcon = () => (
  <span className={styles.editorToolbarTx} aria-hidden="true">
    T<sub>x</sub>
  </span>
);

interface ToolbarAction {
  hint: string;
  icon: React.ReactNode;
  isActive?: boolean;
  onAct: () => void;
}

function PromptEditorToolbar() {
  const editor = useSlate();
  const [isLinkModalVisible, setIsLinkModalVisible] = useState(false);
  const [url, setUrl] = useState("");

  const act = (action: () => void) => (event: React.MouseEvent) => {
    event.preventDefault();
    action();
  };

  const action = ({ hint, icon, isActive, onAct }: ToolbarAction) => (
    <Tooltip key={hint} title={hint}>
      <Button
        className={classNames(styles.editorToolbarButton, { [styles.editorToolbarButtonActive]: isActive })}
        type="text"
        size="small"
        aria-label={hint}
        aria-pressed={isActive}
        icon={icon}
        onMouseDown={act(onAct)}
      />
    </Tooltip>
  );

  const headingLevel = activeHeadingLevel(editor);

  /**
   * 🔴 EVERY ITEM ACTS ON MOUSEDOWN WITH THE DEFAULT PREVENTED, like the buttons beside it. A menu item that
   * waits for the click has already let the browser move the focus out of the editor, and with it the
   * selection the transform needs.
   */
  const headingMenu = (
    <Menu selectedKeys={[headingLevel ? String(headingLevel) : "normal"]}>
      <Menu.Item key="normal" onMouseDown={act(() => setHeading(editor))}>
        {HEADING_NORMAL_LABEL}
      </Menu.Item>
      {HEADING_LEVELS.map((level) => (
        <Menu.Item key={String(level)} onMouseDown={act(() => setHeading(editor, level))}>
          <span className={styles.editorToolbarHeadingItem} style={{ fontSize: 20 - level }}>
            {HEADING_LABEL(level)}
          </span>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div className={styles.editorToolbar}>
      <div className={styles.editorToolbarGroup}>
        {/*
          🔴 NO TOOLTIP ON THIS ONE, UNLIKE THE ICONS. It carries its own label — "Normal text" or "H3" — so a
          tooltip would only repeat it, and while the menu is open antd keeps the tooltip up: it sat directly
          over the menu's first row. The `aria-label` stays, since the label changes as the caret moves.
        */}
        <Dropdown
          overlay={headingMenu}
          overlayClassName={styles.editorToolbarHeadingMenu}
          placement="bottomLeft"
          trigger={["click"]}
        >
          <Button className={styles.editorToolbarHeading} type="text" size="small" aria-label={HEADING_HINT}>
            {headingLevel ? HEADING_LABEL(headingLevel) : HEADING_NORMAL_LABEL}
            <DownOutlined className={styles.editorToolbarHeadingCaret} />
          </Button>
        </Dropdown>
      </div>

      <div className={styles.editorToolbarGroup}>
        {MARKS.map(({ mark, icon }) =>
          action({
            hint: MARK_HINT[mark],
            icon,
            isActive: isMarkActive(editor, mark),
            onAct: () => toggleMark(editor, mark),
          })
        )}
      </div>

      <div className={styles.editorToolbarGroup}>
        {LISTS.map(({ type, icon, hint }) =>
          action({ hint, icon, isActive: isListActive(editor, type), onAct: () => toggleList(editor, type) })
        )}
      </div>

      <div className={styles.editorToolbarGroup}>
        {action({
          hint: INSERT_LINK_HINT,
          icon: <LinkOutlined />,
          onAct: () => setIsLinkModalVisible(true),
        })}
        {action({
          hint: CLEAR_FORMATTING_HINT,
          icon: <ClearFormattingIcon />,
          onAct: () => clearFormatting(editor),
        })}
      </div>

      <EComModal
        visible={isLinkModalVisible}
        title={LINK_MODAL_TITLE}
        okText={LINK_MODAL_OK_TEXT}
        onOk={() => {
          insertLink(editor, url.trim());
          setUrl("");
          setIsLinkModalVisible(false);
        }}
        onCancel={() => {
          setUrl("");
          setIsLinkModalVisible(false);
        }}
      >
        <Input
          value={url}
          placeholder={LINK_URL_PLACEHOLDER}
          onChange={(event) => setUrl(event.target.value)}
          autoFocus
        />
      </EComModal>
    </div>
  );
}

export default PromptEditorToolbar;
